<template>
  <div :class="['content', programStyle]">
    <div class="title-container">
      <div class="title">CHARGING MANAGEMENT SOFTWARE</div>
    </div>
    <div v-if="mostRecentStatus && mostRecentStatus.result && mostRecentStatus.reason" class="review-container">
      <div :class="['result-container', mostRecentStatus.result === 'Declined' ? 'red' : 'green']">
        {{ mostRecentStatus.result }}
      </div>
      <div class="reason">{{ $t('reason', [mostRecentStatus.reason]) }}</div>
    </div>
    <div class="form-container">
      <div class="item-container" v-for="(appeal, index) in appeals" :key="appeal.dialId">
        <div class="dial-info">
          <div class="left-container flex-row">
            <div v-if="index === 0" :class="['circle', appeal.dialColor]">
              <div class="inner" v-if="appeal.dialResultText">
                {{ appeal.dialResultText }}
              </div>
            </div>
          </div>
          <div class="right-container flex-column">
            <div v-if="index === 0" class="title-small">* PROOF OF ACTIVE CHARGING MANAGEMENT SOFTWARE</div>
            <div class="subtitle">{{ appeal.dialTitle }}</div>
          </div>
        </div>
        <div class="upload-container">
          <Upload
            :class="[validationCheck && !uploadsValidation ? 'uploads-required' : '']"
            v-if="!appeal.uploads"
            :appeal="appeal"
            v-on:sendurl="getUrl"
            @removeUpload="removeUpload"
          />
          <div v-if="validationCheck && !uploadsValidation" class="helper-text_error">
            <h4>You must upload photos/documentation.</h4>
          </div>
          <div v-if="appeal.uploads">
            <h4 class="title">{{ $t('supporting-documents-0') }}</h4>
            <div class="support-docs" v-for="upload in appeal.uploads" :key="upload.key">
              <span v-if="upload.dialId === appeal.dialId">{{ upload.originalFilename }}</span>
              <a v-if="upload.dialId === appeal.dialId" class="relative" target="_blank" :href="upload.url">
                <img class="download-icon" src="@/assets/img/download-arrow.png" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="bottom-container flex-column">
        <div class="form-details flex-column">
          <h4 class="title">Details</h4>
          <div class="row-1 flex-row">
            <ul>
              <li class="input-label"><label for="custom-input">* INSTALLER/VALIDATOR VENDOR:</label></li>
              <li class="custom-input xl flex-row" :class="[validationCheck && !vendor ? 'required' : '']">
                <input :class="[viewForm ? 'disabled' : '']" required v-model="vendor" :placeholder="'Enter name'" />
                <span class="custom-input-buttons flex-row" v-if="!viewForm">
                  <span class="edit-button" @click="vendor = null">{{ $t('clear') }}</span>
                </span>
              </li>
            </ul>
            <ul>
              <li class="input-label"><label for="custom-input">* CHARGING MANAGEMENT SOFTWARE PROVIDER:</label></li>
              <li class="custom-input xl flex-row" :class="[validationCheck && !cpms ? 'required' : '']">
                <input :class="[viewForm ? 'disabled' : '']" required v-model="cpms" :placeholder="'Enter name'" />
                <span class="custom-input-buttons flex-row" v-if="!viewForm">
                  <span class="edit-button" @click="cpms = null">{{ $t('clear') }}</span>
                </span>
              </li>
            </ul>
          </div>
          <h4 class="note">
            Note: After proof of active charging management software has been uploaded, it will be reviewed by
            Stellantis. When the review is complete, the associated pillar dial will be updated the next business day.
          </h4>
        </div>
        <div class="button-container flex-row">
          <button
            class="submit-button"
            :class="[submitValidation ? '' : 'disabled']"
            v-if="!viewForm"
            @click="validateEverything"
          >
            {{ $t('submit') }}
          </button>
          <button class="exit-button" v-if="!viewForm" @click="isModalOpen = true">{{ $t('exit') }}</button>
          <button class="exit-button" v-if="viewForm" @click="exit">{{ $t('exit') }}</button>
        </div>
      </div>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        <div class="modal-content">
          <span class="modal-title">Confirm Cancellation?</span>
          <span class="modal-text"
            >You are cancelling this request. This action cannot be undone. Are you sure you want to confirm your
            cancellation?</span
          >
        </div>
        <div class="modal-button-container">
          <button class="submit" @click="exit">Confirm</button>
          <button class="cancel-button" @click="isModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Upload from '@/components/Upload.vue';
import Modal from '@/components/Modal.vue';
import { mapState } from 'vuex';

export default {
  name: 'ChargingManagementSoftware',
  components: { Upload, Modal },
  props: {
    appealType: {
      type: String,
      required: true,
    },
    config: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      appeals: [],
      uploads: [],
      // newDate: new Date(),
      isModalOpen: false,
      vendor: null,
      cpms: null,
      date: null,
      helperTextError: false,
      validationCheck: false,
    };
  },
  methods: {
    dateFilter(value, filter) {
      if (value && filter && this.$options.filters[filter]) {
        return this.$options.filters[filter](value);
      }
      return value;
    },
    getComplexFormSupplementalData() {
      const loader = this.$loading.show();

      const { domain, entity } = this;

      let formId = '';
      if (this.appealType === 'submit') {
        formId = 'new';
      } else {
        const id = this.selectedForm[0].formId;
        formId = id;
      }

      this.$store
        .dispatch('getComplexFormSupplementalData', { formId, domain, entity })
        .then((res) => {
          this.vendor = res.vendor;
          this.cpms = res.cpms;
          this.date = res.date;
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    submitForm() {
      const loader = this.$loading.show();

      const items = [];

      const { domain, entity, uploads, vendor, cpms } = this;

      this.appeals.forEach((appeal) => {
        items.push(appeal);
      });

      const params = {
        domain,
        entity,
        items,
        uploads,
        status: 'submit',
        type: this.config.formType,
        layout: this.config.layout,
        result: 'Submitted',
        configId: this.config.id,
        vendor,
        cpms,
      };

      this.$store
        .dispatch('submitForm', params)
        .then(() => {
          this.$notify({
            group: 'primary',
            type: 'success',
            text: 'Charging Form Successfully Submitted!',
          });
          this.validationCheck = false;
          this.exit();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    exit() {
      this.$router.push({
        name: 'Dealer-Forms',
      });
    },
    getUrl(res) {
      const upload = {
        adjustedFilename: res.res.data.fileInfo.adjustedFilename,
        originalFilename: res.res.data.fileInfo.originalFilename,
        label: res.appeal.dialTitle,
        dialId: res.appeal.dialId,
      };
      this.uploads.push(upload);
    },
    getAppeals() {
      if (this.appealType === 'view') {
        this.appeals = JSON.parse(JSON.stringify(this.selectedForm));
        this.getComplexFormSupplementalData();
        this.isAcknowledged = true;
      } else {
        this.appeals = [
          {
            dialColor: 'N',
            dialId: '0',
            dialResultText: '',
            dialTitle:
              'You must upload photos/documentation (e.g., screenshot) showing the required Level 2 and DCFC charging equipment is active.',
          },
        ];
      }
    },
    removeUpload(selectedFile) {
      this.uploads.forEach((file, index) => {
        if (file.originalFilename === selectedFile.file) {
          this.uploads.splice(index, 1);
        }
      });
    },
    validateEverything() {
      this.validationCheck = true;
      if (this.uploadsValidation && this.vendor && this.cpms) {
        this.submitForm();
      } else {
        this.$notify({
          group: 'primary',
          type: 'error',
          text: 'Please double check the highlighted fields and try again.',
        });
      }
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['selectedForm', 'otherAppealInfo']),
    entity() {
      return this.$route.params.entity;
    },
    domain() {
      return this.$route.params.domain;
    },
    mostRecentStatus() {
      if (this.otherAppealInfo) {
        return this.otherAppealInfo.statuses[0];
      }
      return null;
    },
    viewForm() {
      return this.appealType === 'view';
    },
    uploadsValidation() {
      if (this.uploads.length === 0) {
        return false;
      }
      return true;
    },
    submitValidation() {
      if (this.uploads.length > 0 && this.vendor && this.cpms) {
        return true;
      }
      return false;
    },
  },
  watch: {},
  created() {
    this.getAppeals();
  },
  beforeDestroy() {
    this.$emit('exit');
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
  padding: 30px 30px 45px;
  input:focus {
    outline: none;
  }
}
.title {
  color: #243882;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  text-transform: uppercase;
  margin-bottom: 30px;
}
.title-small {
  font-size: 14px;
}
h4 {
  font-size: 14px;
  font-weight: normal;
  text-transform: none;
  &.title {
    margin-top: 0px !important;
  }
}
.item-container {
  margin: 30px 0 35px;
  .dial-info {
    display: flex;
    margin-bottom: 30px;
  }
}
.right-container {
  font-size: 14px;
  font-weight: bold;
  line-height: 20px;
  text-transform: uppercase;
  input {
    height: 100px;
    width: initial;
    background: $input-background;
    border: none;
    margin-bottom: 20px;
  }
  .item {
    display: flex;
    flex-direction: column;
  }
}
.upload-container {
  font-size: 14px;
  .title {
    font-size: 14px;
    color: $primary-black;
    font-weight: bold;
    line-height: 20px;
    text-transform: uppercase;
    margin: 0px;
  }
  .support-docs {
    display: flex;
    align-items: center;
  }
}
.left-container {
  min-width: 80px;
  align-items: center;
  margin-right: 20px;
}
.subtitle {
  font-size: 14px;
  margin-bottom: 10px;
  font-weight: normal;
  text-transform: none;
}
.bottom-container {
  font-size: 14px;
  line-height: 20px;
  span {
    margin-bottom: 20px;
  }
  span:last-child {
    margin-bottom: 0px;
  }
}
.button-container {
  margin-top: 45px;
  align-items: center;
  justify-content: flex-start;
  button {
    margin-right: 15px;
  }
  button:hover {
    text-decoration: underline;
  }
  .exit-button {
    background-color: $primary-white;
    border: 1px solid $primary;
    color: $primary;
  }
}
.helper-text {
  font-size: 14px;
  color: $primary-black;
  &_success {
    color: #008716 !important;
    span {
      color: #008716 !important;
      font-size: 14px !important;
    }
  }
  &_error {
    color: $error !important;
    h4 {
      color: $error !important;
      font-size: 14px !important;
      line-height: 20px !important;
      margin: 15px 0px 0px !important;
      text-transform: none;
    }
  }
  &_notify {
    color: $primary-black;
    opacity: 0.5 !important;
    font-size: 14px !important;
  }
}
.required {
  border: 1px solid red !important;
}
ul {
  list-style-type: none;
  margin: 0px;
  padding: 0px 30px 0px 0px;
  .input-label {
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .sm {
    width: 200px !important;
  }
  .md {
    width: 239px !important;
  }
  .lg {
    width: 373px !important;
  }
  .xl {
    width: 550px !important;
  }
  li[disabled] {
    background: $input-readonly;
  }
  span[disabled] {
    pointer-events: none;
    opacity: 0;
  }
  .vendor-note {
    margin-top: 10px;
  }
}
button {
  &.disabled {
    pointer-events: none;
  }
}
button:hover {
  text-decoration: underline;
}
.row-1 {
  ul:last-child {
    padding-right: 0px;
  }
}
.custom-input {
  margin-top: 15px;
  border: 1px solid $border;
  width: 270px;
  height: 47px;
  justify-content: space-between;
  input {
    border: none;
    padding: 0px 15px;
    height: 100%;
  }
  input.disabled {
    pointer-events: none;
    background: #d1d1d1;
    color: $input-readonly-text;
  }
  .custom-input-buttons {
    padding: 0px 15px;
    align-items: center;
    .edit-button {
      font-size: 14px;
      color: #243882;
      cursor: pointer;
    }
    .disabled {
      opacity: 0.7;
      cursor: default;
      pointer-events: none;
    }
  }
}
.note {
  margin: 30px 0px 0px;
}
::v-deep {
  .filter {
    width: 254px !important;
    border: none !important;
  }
  .collapsible {
    padding-right: 0px !important;
  }
}
.modal-body {
  display: flex;
  flex-direction: column;
  .close {
    display: flex;
    justify-content: flex-end;
    svg {
      height: 30px;
      width: 30px;
      cursor: pointer;
    }
  }
  .modal-content {
    width: 100%;
    text-align: center;
  }
  .modal-title {
    font-size: 20px;
    line-height: 26px;
    color: $primary;
    text-transform: uppercase;
    font-weight: bold;
    display: block;
    margin-bottom: 20px;
  }
  .modal-text {
    font-size: 14px;
    line-height: 20px;
    color: $primary-black;
    display: block;
  }
  .modal-button-container {
    display: flex;
    margin-top: 35px;
    justify-content: center;
    .submit {
      margin-right: 20px;
    }
    .cancel-button {
      background-color: #ffffff;
      border: 1px solid #243882;
      color: #243882;
    }
  }
}
</style>
